// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  PropsWithChildren,
} from "react";
import {
  auth,
  db,
  perf,
} from "../services/firebase";
import {
  User,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import {
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { trace } from "firebase/performance";
import { createUserDocument } from "../services/user";

interface SubscriptionData {
  basePlan: string;
  addOns: string[];
  status: string;
  expiry: any;
  quotas: {
    pdfReads: number;
    audioMinutes: number;
    videoMinutes: number;
  };
}

interface UsageData {
  pdfReadsThisMonth: number;
  audioMinutesThisMonth: number;
  videoMinutesThisMonth: number;
}

interface UserProfile {
  subscription?: SubscriptionData;
  usage?: UsageData;
  likedStories?: string[];
  readStories?: string[];
  displayName?: string;
  email?: string;
  hasVoted?: boolean;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loadingProfile: boolean;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  updateNickname: (
    userId: string,
    newNickname: string
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      "❌ useAuth() muss innerhalb eines AuthProviders verwendet werden!"
    );
  }
  return context;
};

export const AuthProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] =
    useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] =
    useState<boolean>(true);

  const googleProvider = new GoogleAuthProvider();

  const signInWithGoogle = async () => {
    const t = trace(perf, "auth_signin_with_google");
    t.start();
    try {
      const result = await signInWithPopup(
        auth,
        googleProvider
      );
      const u = result.user;
      await createUserDocument(
        u.uid,
        u.email || "",
        u.displayName || ""
      );
      t.stop();
    } catch (err) {
      t.stop();
      console.error("Google Sign-In Fehler:", err);
      throw err;
    }
  };

  const updateNickname = async (
    userId: string,
    newNickname: string
  ) => {
    const t = trace(perf, "auth_update_nickname");
    t.start();
    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, {
        displayName: newNickname,
      });
      t.stop();
    } catch (err) {
      t.stop();
      console.error(
        "Fehler beim Aktualisieren des Nicknames:",
        err
      );
      throw new Error(
        "Nicknamen-Aktualisierung fehlgeschlagen."
      );
    }
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(
      auth,
      (currentUser) => {
        const t = trace(perf, "auth_state_changed");
        t.start();
        setUser(currentUser);
        t.stop();
        if (currentUser) {
          setLoadingProfile(true);
          const userDocRef = doc(
            db,
            "users",
            currentUser.uid
          );
          const unsubscribeProfile = onSnapshot(
            userDocRef,
            (snap) => {
              const t2 = trace(
                perf,
                "auth_profile_loaded"
              );
              t2.start();
              setUserProfile(
                snap.exists()
                  ? (snap.data() as UserProfile)
                  : null
              );
              setLoadingProfile(false);
              t2.stop();
            },
            (err) => {
              console.error(
                "Profile-Listener error:",
                err
              );
              setUserProfile(null);
              setLoadingProfile(false);
            }
          );
          return () => unsubscribeProfile();
        } else {
          setUserProfile(null);
          setLoadingProfile(false);
        }
      }
    );
    return () => unsubscribeAuth();
  }, []);

  const logout = async () => {
    const t = trace(perf, "auth_logout");
    t.start();
    try {
      await signOut(auth);
      t.stop();
    } catch (error) {
      t.stop();
      console.error("❌ Fehler beim Logout:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        loadingProfile,
        logout,
        signInWithGoogle,
        updateNickname,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
